(() => {
  const root = () => window.routes?.root_url || window.Shopify?.routes?.root || '/';
  const money = (cents) => window.WakeoryDTC?.money?.(Number(cents || 0)) || `${(Number(cents || 0) / 100).toFixed(2)}`;
  const storage = {
    get(key, fallback = []) { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (_) { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} }
  };

  const fetchProduct = async (handle) => {
    const response = await fetch(`${root()}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) throw new Error('Product unavailable');
    return response.json();
  };

  const productCard = (product, options = {}) => {
    const handle = product.handle;
    const image = product.featured_image || product.images?.[0] || '';
    const price = product.price ?? product.variants?.[0]?.price ?? 0;
    const compare = product.compare_at_price ?? product.variants?.[0]?.compare_at_price ?? 0;
    const link = `${root()}products/${handle}`;
    const sale = Number(compare) > Number(price);
    return `
      <article class="wy-async-product" data-handle="${handle}">
        <a class="wy-async-product__media" href="${link}">${image ? `<img src="${image}${image.includes('?') ? '&' : '?'}width=700" loading="lazy" alt="">` : ''}</a>
        ${options.wishlist ? `<button type="button" class="wy-wishlist-button" data-wy-wishlist data-handle="${handle}" aria-label="Save ${product.title || ''}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.7-7.5 1.1-1.1a5.5 5.5 0 0 0 0-7.8Z"/></svg></button>` : ''}
        ${sale ? `<div class="wy-product-badges"><span class="wy-product-badge">Sale</span></div>` : ''}
        <div class="wy-async-product__content">
          <h3 class="wy-async-product__title"><a href="${link}">${product.title || handle}</a></h3>
          <div class="wy-async-product__price"><span>${money(price)}</span>${sale ? `<span class="wy-async-product__compare">${money(compare)}</span>` : ''}</div>
        </div>
      </article>`;
  };

  const wishlistHandles = () => storage.get('wy-wishlist', []);
  const currentProductHandle = document.body?.dataset?.wyProductHandle;
  if (currentProductHandle) {
    const recent = storage.get('wy-recent-products', []).filter((handle) => handle !== currentProductHandle);
    recent.unshift(currentProductHandle);
    storage.set('wy-recent-products', recent.slice(0, 12));
  }
  const syncWishlistButtons = () => {
    const active = new Set(wishlistHandles());
    document.querySelectorAll('[data-wy-wishlist]').forEach((button) => {
      const selected = active.has(button.dataset.handle);
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
      const label = button.querySelector('[data-wy-wishlist-label]');
      if (label) label.textContent = selected ? (button.dataset.removeLabel || 'Saved') : (button.dataset.addLabel || 'Save');
    });
    document.querySelectorAll('[data-wy-wishlist-count]').forEach((node) => { node.textContent = active.size; node.hidden = active.size === 0; });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wy-wishlist]');
    if (!button) return;
    event.preventDefault();
    const items = new Set(wishlistHandles());
    items.has(button.dataset.handle) ? items.delete(button.dataset.handle) : items.add(button.dataset.handle);
    storage.set('wy-wishlist', [...items]);
    syncWishlistButtons();
    document.dispatchEvent(new CustomEvent('wakeory:wishlist-change', { detail: { handles: [...items] } }));
  });

  class WyAsyncProductGrid extends HTMLElement {
    async connectedCallback() {
      const mode = this.dataset.mode || 'recent';
      if (mode === 'wishlist' && !this.wishlistListener) {
        this.wishlistListener = () => this.connectedCallback();
        document.addEventListener('wakeory:wishlist-change', this.wishlistListener);
      }
      const current = this.dataset.currentHandle || '';
      if (current && mode === 'recent') {
        const recent = storage.get('wy-recent-products', []).filter((handle) => handle !== current);
        recent.unshift(current);
        storage.set('wy-recent-products', recent.slice(0, 12));
      }
      const source = mode === 'wishlist' ? wishlistHandles() : storage.get('wy-recent-products', []).filter((handle) => handle !== current);
      const handles = source.slice(0, Number(this.dataset.limit || 4));
      if (!handles.length) return this.renderEmpty();
      const results = await Promise.allSettled(handles.map(fetchProduct));
      const products = results.filter((item) => item.status === 'fulfilled').map((item) => item.value);
      if (!products.length) return this.renderEmpty();
      this.innerHTML = products.map((product) => productCard(product, { wishlist: this.dataset.wishlist === 'true' })).join('');
      syncWishlistButtons();
    }
    renderEmpty() {
      const message = this.dataset.empty || '';
      this.innerHTML = message ? `<div class="wy-async-products__empty">${message}</div>` : '';
    }
  }

  class WyPopup extends HTMLElement {
    connectedCallback() {
      this.key = `wy-popup-${this.dataset.id || 'global'}`;
      if (this.dataset.designMode !== 'true' && this.isSuppressed()) return;
      this.querySelectorAll('[data-wy-popup-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.addEventListener('click', (event) => { if (event.target === this) this.close(); });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      const trigger = this.dataset.trigger || 'delay';
      if (this.dataset.designMode === 'true') return;
      if (trigger === 'scroll') {
        this.onScroll = () => {
          const max = document.documentElement.scrollHeight - innerHeight;
          if (max > 0 && scrollY / max >= Number(this.dataset.scroll || 35) / 100) this.open();
        };
        addEventListener('scroll', this.onScroll, { passive: true });
      } else if (trigger === 'exit') {
        this.onExit = (event) => { if (this.dataset.exitReady === 'true' && event.clientY <= 8) this.open(); };
        document.addEventListener('mouseout', this.onExit);
        this.timer = setTimeout(() => this.dataset.exitReady = 'true', 2500);
      } else {
        this.timer = setTimeout(() => this.open(), Number(this.dataset.delay || 5) * 1000);
      }
    }
    isSuppressed() {
      const until = Number(localStorage.getItem(this.key) || 0);
      return until > Date.now();
    }
    open() {
      if (this.classList.contains('is-open') || this.isSuppressed()) return;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('wy-v2-lock');
      this.querySelector('input, button')?.focus({ preventScroll: true });
      this.cleanupTriggers();
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('wy-v2-lock');
      const days = Number(this.dataset.frequency || 7);
      localStorage.setItem(this.key, String(Date.now() + days * 86400000));
    }
    cleanupTriggers() {
      clearTimeout(this.timer);
      if (this.onScroll) removeEventListener('scroll', this.onScroll);
      if (this.onExit) document.removeEventListener('mouseout', this.onExit);
    }
  }

  class WyAgeGate extends HTMLElement {
    connectedCallback() {
      const key = `wy-age-gate-${this.dataset.id || 'global'}`;
      if (this.dataset.designMode !== 'true' && localStorage.getItem(key) === 'accepted') return this.remove();
      this.classList.add('is-open');
      document.body.classList.add('wy-v2-lock');
      this.querySelector('[data-wy-age-accept]')?.addEventListener('click', () => {
        localStorage.setItem(key, 'accepted');
        this.classList.remove('is-open');
        document.body.classList.remove('wy-v2-lock');
        setTimeout(() => this.remove(), 300);
      });
    }
  }

  class WyMobileDock extends HTMLElement {
    connectedCallback() {
      document.body.classList.add('wy-has-mobile-dock');
      const update = () => {
        const source = document.querySelector('#cart-icon-bubble .cart-count-bubble span:not(.visually-hidden)');
        const count = source?.textContent?.trim() || '0';
        this.querySelectorAll('[data-wy-dock-cart-count]').forEach((node) => { node.textContent = count; node.hidden = count === '0'; });
      };
      update();
      const target = document.getElementById('cart-icon-bubble');
      if (target) new MutationObserver(update).observe(target, { childList: true, subtree: true, characterData: true });
      this.querySelector('[data-wy-open-cart]')?.addEventListener('click', () => document.querySelector('cart-drawer')?.open(this));
    }
  }

  class WyHotspots extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-hotspot-button]')];
      this.cards = [...this.querySelectorAll('[data-hotspot-card]')];
      this.buttons.forEach((button, index) => button.addEventListener('click', () => this.select(index)));
      if (this.buttons.length) this.select(0);
    }
    select(index) {
      this.buttons.forEach((button, i) => { button.setAttribute('aria-expanded', String(i === index)); button.textContent = i === index ? '−' : '+'; });
      this.cards.forEach((card, i) => card.classList.toggle('is-active', i === index));
    }
  }

  class WyFinder extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('[data-finder-step]')];
      this.results = [...this.querySelectorAll('[data-finder-result]')];
      this.index = 0; this.scores = {};
      this.querySelectorAll('[data-finder-answer]').forEach((button) => button.addEventListener('click', () => this.answer(button.dataset.resultKey)));
      this.querySelector('[data-finder-restart]')?.addEventListener('click', () => this.restart());
      this.showStep(0);
    }
    answer(key) {
      if (key) this.scores[key] = (this.scores[key] || 0) + 1;
      this.index += 1;
      if (this.index >= this.steps.length) return this.finish();
      this.showStep(this.index);
    }
    showStep(index) {
      this.steps.forEach((step, i) => step.hidden = i !== index);
      this.results.forEach((result) => result.hidden = true);
      this.style.setProperty('--wy-progress', `${Math.round((index / Math.max(this.steps.length, 1)) * 100)}%`);
    }
    finish() {
      const winning = Object.entries(this.scores).sort((a,b) => b[1] - a[1])[0]?.[0] || this.results[0]?.dataset.resultKey;
      this.steps.forEach((step) => step.hidden = true);
      this.results.forEach((result) => result.hidden = result.dataset.resultKey !== winning);
      this.style.setProperty('--wy-progress', '100%');
    }
    restart() { this.index = 0; this.scores = {}; this.showStep(0); }
  }

  class WyCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.buttons.forEach((button, index) => button.addEventListener('click', () => this.select(index)));
      this.select(0);
    }
    select(index) {
      this.buttons.forEach((button, i) => { button.setAttribute('aria-selected', String(i === index)); button.tabIndex = i === index ? 0 : -1; });
      this.panels.forEach((panel, i) => panel.hidden = i !== index);
    }
  }

  class WyQuantityBreaks extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-quantity]')];
      this.buttons.forEach((button) => button.addEventListener('click', () => this.select(button)));
      this.select(this.querySelector('.is-active') || this.buttons[0]);
    }
    select(button) {
      if (!button) return;
      this.buttons.forEach((item) => item.classList.toggle('is-active', item === button));
      const quantity = Number(button.dataset.quantity || 1);
      const selector = this.dataset.quantityInput;
      const input = selector ? document.querySelector(selector) : document.querySelector('.product-form__quantity input[name="quantity"]');
      if (input) { input.value = quantity; input.dispatchEvent(new Event('change', { bubbles: true })); }
    }
  }

  const addItems = async (items, trigger) => {
    const cart = document.querySelector('cart-drawer');
    const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections, sections_url: location.pathname })
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || 'Unable to add items');
    if (cart && data.sections) { cart.setActiveElement(trigger); cart.renderContents(data); }
    else location.href = window.routes?.cart_url || '/cart';
    return data;
  };

  document.addEventListener('click', async (event) => {
    const sound = event.target.closest('[data-wy-video-sound]');
    if (sound) {
      const video = sound.closest('.wy-shop-video')?.querySelector('video');
      if (video) { video.muted = !video.muted; sound.classList.toggle('is-active', !video.muted); }
      return;
    }
    const quickAdd = event.target.closest('[data-wy-quick-add]');
    if (!quickAdd || quickAdd.disabled) return;
    quickAdd.disabled = true;
    try { await addItems([{ id: Number(quickAdd.dataset.wyQuickAdd), quantity: 1 }], quickAdd); }
    finally { quickAdd.disabled = false; }
  });

  class WyFbt extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-fbt-add]');
      this.button?.addEventListener('click', async () => {
        const items = [...this.querySelectorAll('[data-fbt-item]:checked')].map((input) => {
          const currentId = document.getElementById(this.dataset.productForm)?.querySelector('[name="id"]')?.value || document.querySelector('product-form form [name="id"]')?.value;
          return { id: Number(input.hasAttribute('data-fbt-current') ? currentId : input.value), quantity: 1 };
        }).filter((item) => item.id);
        if (!items.length) return;
        this.button.disabled = true;
        const label = this.button.textContent;
        this.button.textContent = this.button.dataset.loading || 'Adding…';
        try { await addItems(items, this.button); }
        catch (error) { this.querySelector('[data-fbt-error]').textContent = error.message; }
        finally { this.button.disabled = false; this.button.textContent = label; }
      });
    }
  }

  class WyLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.next = this.dataset.next;
      this.button?.addEventListener('click', () => this.load());
      if (this.dataset.mode === 'infinite' && this.next) {
        this.observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) this.load(); }, { rootMargin: '350px' });
        this.observer.observe(this);
      }
    }
    async load() {
      if (!this.next || this.loading) return;
      this.loading = true; this.button.disabled = true;
      const response = await fetch(this.next);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('#product-grid');
      const current = document.querySelector('#product-grid');
      incoming?.querySelectorAll(':scope > li').forEach((item) => current?.appendChild(item));
      const nextControl = doc.querySelector('wy-load-more');
      this.next = nextControl?.dataset.next || '';
      this.dataset.next = this.next;
      this.loading = false; this.button.disabled = false;
      if (!this.next) { this.remove(); this.observer?.disconnect(); }
    }
  }

  class WyCartDiscount extends HTMLElement {
    connectedCallback() {
      this.querySelector('form')?.addEventListener('submit', (event) => {
        event.preventDefault();
        const code = new FormData(event.currentTarget).get('discount')?.trim();
        if (code) location.href = `${root()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.routes?.cart_url || '/cart')}`;
      });
    }
  }

  class WyCartAddon extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.input?.addEventListener('change', async () => {
        this.input.disabled = true;
        try {
          if (this.input.checked) await addItems([{ id: Number(this.input.value), quantity: 1 }], this.input);
          else {
            const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: this.input.dataset.key, quantity: 0, sections: ['cart-drawer','cart-icon-bubble'], sections_url: location.pathname }) });
            const data = await response.json(); document.querySelector('cart-drawer')?.renderContents(data);
          }
        } catch (_) { this.input.checked = !this.input.checked; }
        finally { this.input.disabled = false; }
      });
    }
  }

  const definitions = {
    'wy-async-product-grid': WyAsyncProductGrid,
    'wy-popup': WyPopup,
    'wy-age-gate': WyAgeGate,
    'wy-mobile-dock': WyMobileDock,
    'wy-hotspots': WyHotspots,
    'wy-finder': WyFinder,
    'wy-collection-tabs': WyCollectionTabs,
    'wy-quantity-breaks': WyQuantityBreaks,
    'wy-fbt': WyFbt,
    'wy-load-more': WyLoadMore,
    'wy-cart-discount': WyCartDiscount,
    'wy-cart-addon': WyCartAddon
  };
  Object.entries(definitions).forEach(([name, component]) => { if (!customElements.get(name)) customElements.define(name, component); });

  document.addEventListener('DOMContentLoaded', syncWishlistButtons);
  document.addEventListener('shopify:section:load', syncWishlistButtons);
})();
